import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  Server,
  Loader2,
} from "lucide-react";
import {
  useAdminRegistrarSettings,
  useTestRegistrarConnectivity,
} from "@/hooks/useAdminRegistrarQueries";
import type { RegistrarConnectivityStatus } from "@/types/domain";

/** Maps a connectivity status to a Badge variant. */
function connectivityVariant(
  status: RegistrarConnectivityStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "Connected":
      return "default";
    case "Unreachable":
      return "destructive";
    default:
      return "outline";
  }
}

/** Picks an icon for a connectivity status. */
function ConnectivityIcon({ status }: { status: RegistrarConnectivityStatus }) {
  switch (status) {
    case "Connected":
      return <CheckCircle2 className="h-5 w-5 text-ok-ink" />;
    case "Unreachable":
      return <XCircle className="h-5 w-5 text-destructive-ink" />;
    default:
      return <HelpCircle className="h-5 w-5 text-muted-foreground" />;
  }
}

export function RegistrarSettingsPage() {
  const { data, isLoading, error, refetch } = useAdminRegistrarSettings();
  const testConnectivity = useTestRegistrarConnectivity();

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Registrar Settings"
          subtitle="Active registrar provider and connectivity status"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">
                Failed to load registrar settings
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Registrar Settings"
        subtitle="Active registrar provider and connectivity status"
        actions={[
          {
            label: "Test Connectivity",
            onClick: () => testConnectivity.mutate(),
            variant: "outline",
            disabled: testConnectivity.isPending || isLoading,
            icon: testConnectivity.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : undefined,
          },
          {
            label: "Refresh",
            onClick: () => refetch(),
            variant: "outline",
            disabled: isLoading,
          },
        ]}
      />

      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Loading registrar settings...
              </p>
            </div>
          </CardContent>
        </Card>
      ) : !data ? (
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                No registrar is configured
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Connectivity Card */}
          <Card>
            <CardHeader>
              <CardTitle>Connectivity</CardTitle>
              <CardDescription>
                Current registrar connectivity indicator
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <ConnectivityIcon status={data.connectivityStatus} />
                <Badge variant={connectivityVariant(data.connectivityStatus)}>
                  {data.connectivityStatus}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Card */}
          <Card>
            <CardHeader className="flex flex-row items-center gap-2 space-y-0">
              <Server className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Active Provider</CardTitle>
                <CardDescription>
                  Read-only configuration summary — secrets are never displayed
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Configured Provider
                  </dt>
                  <dd className="text-base">{data.configuredProvider}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Active Provider
                  </dt>
                  <dd className="text-base">{data.activeProvider}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-muted-foreground">
                    Default Nameservers
                  </dt>
                  <dd className="text-base">
                    {data.defaultNameservers.length > 0 ? (
                      <ul className="mt-1 space-y-1">
                        {data.defaultNameservers.map((ns) => (
                          <li key={ns} className="font-mono text-sm">
                            {ns}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground">
                        No default nameservers configured
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
